import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ReferralStats {
  total_referrals: number;
  pending_referrals: number;
  completed_referrals: number;
  rewards_earned: number;
}

export const useReferral = () => {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const loadReferralData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      setUserId(session.user.id);

      // Get or create referral code
      const { data: code, error: codeError } = await supabase.rpc(
        "get_or_create_referral_code",
        { p_user_id: session.user.id }
      );

      if (!codeError && code) {
        setReferralCode(code);
      }

      // Get referral stats
      const { data: statsData, error: statsError } = await supabase.rpc(
        "get_referral_stats",
        { p_user_id: session.user.id }
      );

      if (!statsError && statsData) {
        // statsData is returned as an array with single row
        const statsRow = Array.isArray(statsData) ? statsData[0] : statsData;
        setStats(statsRow || {
          total_referrals: 0,
          pending_referrals: 0,
          completed_referrals: 0,
          rewards_earned: 0
        });
      }

      setLoading(false);
    };

    loadReferralData();
  }, []);

  const applyReferralCode = async (code: string): Promise<boolean> => {
    if (!userId) return false;

    const { data, error } = await supabase.rpc("apply_referral_code", {
      p_referred_id: userId,
      p_code: code.toUpperCase(),
    });

    if (error) {
      console.error("Error applying referral code:", error);
      return false;
    }

    return data === true;
  };

  const getShareUrl = () => {
    if (!referralCode) return null;
    return `${window.location.origin}/auth?mode=signup&ref=${referralCode}`;
  };

  const getShareText = () => {
    return `Découvrez EasyFinder, l'assistant intelligent pour gérer tous vos documents ! Utilisez mon code ${referralCode} pour vous inscrire.`;
  };

  return {
    referralCode,
    stats,
    loading,
    applyReferralCode,
    getShareUrl,
    getShareText,
  };
};
